import { useEffect, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { Netzstreifen } from '../../components/Netzstreifen'
import { rueckfrage } from '../../components/Rueckfrage'
import { Kontoloeschung } from './Kontoloeschung'

import {
  useMe,
  usePeople,
  useSetDisabled,
  useSetPersonName,
  useSetRole,
} from '../../hooks/queries'
import type { AccountRole, Person } from '../../api/types'
import { BackIcon } from '../../components/Icons'

/**
 * Wer darf eigene Reiseberichte schreiben.
 *
 * Sichtbar nur für die Verwaltung — für alle anderen wäre diese Liste ein
 * Verzeichnis aller Konten. Der Server verweigert sie ohnehin; hier wird sie
 * gar nicht erst angefragt.
 */
export function PeoplePage() {
  const me = useMe()
  const darfVerwalten = me.data?.may_manage_people === true
  const people = usePeople(darfVerwalten)
  const setRole = useSetRole()
  const setDisabled = useSetDisabled()
  const setName = useSetPersonName()

  if (me.data && !darfVerwalten) return <Navigate to="/settings" replace />

  return (
    <>
      <header className="bar">
        <Link className="icon-button" to="/settings" aria-label="Zurück">
          <BackIcon />
        </Link>
        <h2 className="bar__title bar__grow">Personen</h2>
      </header>
      <Netzstreifen />

      <div className="scroll-panel">
        <div className="card card--seite">
          <p className="card__lead" style={{ marginBottom: 0 }}>
            Wer über einen Einladungslink kommt, liest mit und antwortet. Zum
            Schreiben eines eigenen Reiseberichts gehört eine Freischaltung.
            Der Name für die Verwaltung steht nur hier und in deinen eigenen
            Ansichten — die Mitlesenden sehen ihn nie.
          </p>
        </div>

        {(setRole.isError || setDisabled.isError || setName.isError) && (
          <p className="notice" role="alert" style={{ maxWidth: 520, margin: '0 auto 16px' }}>
            {(setRole.error ?? setDisabled.error ?? setName.error)?.message}
          </p>
        )}

        <div className="card card--seite" style={{ marginBottom: 0 }}>
          {people.isPending && <p className="card__lead">Wird geladen …</p>}

          <div className="stack">
            {people.data?.map((person) => (
              <PersonRow
                key={person.id}
                person={person}
                self={person.id === me.data?.id}
                busy={
                  setRole.isPending ||
                  setDisabled.isPending ||
                  setName.isPending
                }
                onChange={(role) => setRole.mutate({ id: person.id, role })}
                onDisabled={(disabled) => setDisabled.mutate({ id: person.id, disabled })}
                onName={(name) => setName.mutate({ id: person.id, name })}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  )
}

function PersonRow({
  person,
  self,
  busy,
  onChange,
  onDisabled,
  onName,
}: {
  person: Person
  self: boolean
  busy: boolean
  onChange: (role: AccountRole) => void
  onDisabled: (disabled: boolean) => void
  onName: (name: string) => void
}) {
  // Der Entwurf bleibt im Feld, bis er hinausgeht. Ein Feld, das bei jedem
  // Buchstaben sichert, ist derselbe Fehler wie beim Regler des Deckgrads.
  const [entwurf, setEntwurf] = useState(person.admin_display_name ?? '')
  useEffect(() => {
    setEntwurf(person.admin_display_name ?? '')
  }, [person.admin_display_name])

  const angezeigt = person.admin_display_name ?? person.display_name

  const sichere = () => {
    const wert = entwurf.trim()
    if (wert === (person.admin_display_name ?? '')) return
    onName(wert)
  }

  return (
    <div
      className="stack"
      style={{ gap: 6, paddingBottom: 12, borderBottom: '1px solid var(--border)' }}
    >
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span style={{ fontWeight: 600 }}>
          {angezeigt}
          {self && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> · du</span>}
          {person.disabled && (
            <span style={{ color: 'var(--danger)', fontWeight: 400 }}> · stillgelegt</span>
          )}
        </span>
        <span className="feld__hinweis feld__hinweis--13">
          {person.trips > 0
            ? `${person.trips} ${person.trips === 1 ? 'Reise' : 'Reisen'}`
            : ''}
        </span>
      </div>

      {/* Beide Namen stehen da, sobald sie sich unterscheiden -- sonst wüsste
          man nicht mehr, wie dieser Mensch sich selbst nennt, und suchte ihn
          in seiner eigenen Antwort vergeblich. */}
      {person.admin_display_name !== null && (
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          nennt sich selbst {person.display_name}
        </span>
      )}

      <span style={{ fontSize: 13, color: 'var(--text-muted)', wordBreak: 'break-all' }}>
        {person.email}
      </span>

      {/* **Nicht abgeblendet, solange irgendwo gesichert wird.** Eine
          Vorsichtsregel, die eine Bedienung überstimmt, ist ein kaputter
          Knopf: das Feld verlöre den Fokus mitten im Tippen, und auf dem
          Telefon ginge die Tastatur zu. Gesichert wird beim Verlassen, und
          zwei Anfragen hintereinander schaden hier nichts. */}
      <label className="field" style={{ marginBottom: 0 }}>
        <span className="visually-hidden">
          Name für die Verwaltung von {person.display_name}
        </span>
        {/* **Kein Vorschlag aus den Kontakten** (#28). Aus dem Betrieb: über
            der Tastatur stand „Kontakt autom. ausfüllen" -- iOS hält ein Feld
            mit einem Namen darin für ein Kontaktfeld und bietet die eigene
            Adresskartei an. Hier steht aber ein Name, den die *Verwaltung*
            vergibt; ein Vorschlag aus den eigenen Kontakten trifft nie zu.

            Die Leiste selbst bleibt: sie gehört der Tastatur und nicht der
            Seite, und keine Web-Anwendung kann sie ausblenden. Weg ist nur
            der Vorschlag darin.

            `autocorrect`/`autocapitalize` stehen dabei: ein selbst vergebener
            Name ist kein Fließtext, und eine Autokorrektur, die „Binder" zu
            „Binde" macht, ist hier keine Hilfe. */}
        <input
          className="field__input"
          type="text"
          maxLength={80}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="words"
          spellCheck={false}
          placeholder={`Name für die Verwaltung — sonst ${person.display_name}`}
          value={entwurf}
          onChange={(event) => setEntwurf(event.target.value)}
          onBlur={sichere}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              event.currentTarget.blur()
            }
          }}
        />
      </label>

      {self ? (
        // Die eigene Rolle lässt sich nicht ändern -- sonst könnte sich die
        // letzte Verwaltung selbst aussperren.
        <span className="feld__hinweis feld__hinweis--13">
          {beschreibung(person.role)} · Die eigene Rolle ändert eine zweite
          Verwaltung.
        </span>
      ) : (
        <>
          <label className="field" style={{ marginBottom: 0 }}>
            <span className="visually-hidden">Rolle von {person.display_name}</span>
            <select
              className="field__input"
              value={person.role}
              disabled={busy || person.disabled}
              onChange={(event) => onChange(event.target.value as AccountRole)}
            >
              <option value="reader">Liest mit</option>
              <option value="author">Darf Reisen anlegen</option>
              <option value="admin">Darf Reisen anlegen und verwalten</option>
            </select>
          </label>

          <button
            type="button"
            className="button button--outline"
            style={{ padding: '6px 12px', fontSize: 14 }}
            disabled={busy}
            onClick={async () => {
              // Freigeben braucht keine Rückfrage, Stilllegen schon -- und die
              // Frage soll sagen, was es nicht tut: Reisen und Nachrichten
              // bleiben.
              if (
                person.disabled ||
                (await rueckfrage(
                  `${person.display_name} stilllegen? ` +
                    'Sie wird sofort abgemeldet und kommt nicht mehr herein. ' +
                    'Reisen, Nachrichten und Mitgliedschaften bleiben unberührt — ' +
                    'du kannst sie jederzeit wieder freigeben.',
                  'Stilllegen',
                ))
              ) {
                onDisabled(!person.disabled)
              }
            }}
          >
            {person.disabled ? 'Wieder freigeben' : 'Konto stilllegen'}
          </button>

          {/*
              **Der Knopf wird nicht mehr versteckt (#47).**

              Hier stand „Löschen nur, wo nichts dranhängt" -- und wo etwas
              dranhing, stand gar nichts, und niemand wusste warum. Seit #47
              lässt sich auch ein Konto mit Nachrichten und Fotos löschen, mit
              gezählter Vorschau und abgetippter E-Mail. Die eine Sperre, die
              bleibt, erklärt sich in der Vorschau selbst: wem eine Reise
              gehört, dessen Konto nicht.

              Nur das eigene bleibt ganz außen vor -- dafür gibt es keinen
              Zustand, in dem es sinnvoll wäre.
          */}
          {!self && <Kontoloeschung person={person} />}
        </>
      )}
    </div>
  )
}

function beschreibung(role: AccountRole): string {
  if (role === 'admin') return 'Darf Reisen anlegen und verwalten'
  if (role === 'author') return 'Darf Reisen anlegen'
  return 'Liest mit'
}
